import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "@/core/store";
import {
  FontWeight,
  StyleBackgroundMessage,
  TypePaintingMessage,
  TypeRankPlayerFormat,
} from "@/domains/settings/chat/constants/chat";

export interface ChatSettings {
  isOpenChatByDefault: boolean;
  isCloseChatAfterSendMessage: boolean;
  fontSize: number;
  fontWeight: FontWeight;

  opacityChatModeWithoutAction: boolean;

  opacityText: number;
  chatWidthSize: number;
  chatHeightSize: number;
  opacityBackground: number;

  notifyInRolledUpMode: boolean; // нотификации в свернутом режиме
  autoscroll: boolean;
  animateChat: boolean;

  showSpeakMessages: boolean;
  showRPMessages: boolean;
  showNewsMessages: boolean;
  showGovernmentNews: boolean;
  showFactionMessages: boolean;

  showGovernmentChat: boolean;
  showFamilyMessages: boolean;
  showServerMessages: boolean;
  showRadioMessages: boolean;
  showSms: boolean;

  showCommandTooltip: boolean;

  highlightOwnMessages: boolean;
  messageHeader: boolean;

  showTime: boolean;
  showBadge: boolean;

  typePaintingMessage: TypePaintingMessage;
  styleBackgroundMessage: StyleBackgroundMessage;
  typeRankPlayerFormat: TypeRankPlayerFormat;
}

export interface ChatSettingsState {
  appliedSettings: ChatSettings | null;
  memoizedSettings: ChatSettings | null;
  defaultSettings: ChatSettings;
}

export const defaultSettings: ChatSettings = {
  isOpenChatByDefault: true,
  isCloseChatAfterSendMessage: false,
  fontSize: 14,
  fontWeight: FontWeight.Semibold,

  opacityChatModeWithoutAction: true,

  opacityText: 0,
  chatWidthSize: 30,
  chatHeightSize: 30,
  opacityBackground: 50,

  notifyInRolledUpMode: true,
  autoscroll: true,
  animateChat: true,

  showSpeakMessages: true,
  showRPMessages: false,
  showNewsMessages: false,
  showGovernmentNews: false,
  showFactionMessages: true,

  showGovernmentChat: false,
  showFamilyMessages: false,
  showServerMessages: false,
  showRadioMessages: false,
  showSms: false,

  showCommandTooltip: true,

  highlightOwnMessages: false,
  messageHeader: true,

  showTime: false,
  showBadge: true,

  typePaintingMessage: TypePaintingMessage.OnlyHeader,
  styleBackgroundMessage: StyleBackgroundMessage.WithoutBackground,
  typeRankPlayerFormat: TypeRankPlayerFormat.Text,
};

const initialState: ChatSettingsState = {
  appliedSettings: defaultSettings,
  memoizedSettings: null,
  defaultSettings,
};

const chatSettingsrSlice = createSlice({
  name: "chatSettings",
  initialState,
  reducers: {
    setAppliedSettings: (state, action: PayloadAction<Partial<ChatSettings>>) => {
      state.appliedSettings = { ...state.appliedSettings, ...action.payload };
    },
    setMemoizedSettings: (state, action: PayloadAction<Partial<ChatSettings> | null>) => {
      if (action.payload) {
        state.memoizedSettings = { ...state.memoizedSettings, ...action.payload };
      } else {
        state.memoizedSettings = null;
      }
    },
    reset: (state) => {
      state.appliedSettings = { ...state.memoizedSettings };
    },
  },
});

export const chatSettingsSelector = (state: RootState) => state.chatSettings;
export const appliedChatSettingsSelector = (state: RootState) => state.chatSettings.appliedSettings;
export const memoizedChatSettingsSelector = (state: RootState) =>
  state.chatSettings.memoizedSettings;
export const defaultChatSettingsSelector = (state: RootState) => state.chatSettings.defaultSettings;

export const chatSettingsReducer = chatSettingsrSlice.reducer;

export const { setAppliedSettings, setMemoizedSettings, reset } = chatSettingsrSlice.actions;
